import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Sparkles } from "lucide-react";
import { CompletedTaskItem } from "./CompletedTaskItem";
import type { Task } from "@/types";

interface CompletedTasksProps {
    tasks: Task[];
    onUndo: (id: string) => void;
    isToday: boolean;
    onEditComment?: (
        taskId: string,
        commentId: string,
        newText: string
    ) => void;
    onDeleteComment?: (taskId: string, commentId: string) => void;
    onAddComment?: (taskId: string, text: string) => void;
}

export function CompletedTasks({
    tasks,
    onUndo,
    isToday,
    onEditComment,
    onDeleteComment,
    onAddComment,
}: CompletedTasksProps) {
    const completedTasks = tasks.filter((t) => t.status === "completed");

    if (completedTasks.length === 0) {
        return null;
    }

    return (
        <Card className="border-0 shadow-md bg-gradient-to-br from-green-50/50 to-emerald-50/50 dark:from-green-950/20 dark:to-emerald-950/20">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <div className="p-1.5 bg-yellow-500/10 rounded-lg">
                        <Trophy className="w-4 h-4 text-yellow-600" />
                    </div>
                    Completed
                    <Badge variant="success" className="ml-auto">
                        <Sparkles className="w-3 h-3 mr-1" />
                        {completedTasks.length} done
                    </Badge>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {completedTasks.map((task) => (
                    <CompletedTaskItem
                        key={task.id}
                        task={task}
                        onUndo={() => onUndo(task.id)}
                        showUndo={isToday}
                        onEditComment={
                            onEditComment
                                ? (commentId, text) =>
                                      onEditComment(task.id, commentId, text)
                                : undefined
                        }
                        onDeleteComment={
                            onDeleteComment
                                ? (commentId) =>
                                      onDeleteComment(task.id, commentId)
                                : undefined
                        }
                        onAddComment={
                            onAddComment
                                ? (text) => onAddComment(task.id, text)
                                : undefined
                        }
                    />
                ))}
            </CardContent>
        </Card>
    );
}
